import React, { useEffect, useState } from "react";
import { X, TrendingUp, Sparkles, Download } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";

// Locale-aware integer formatter. AR → Eastern Arabic-Indic digits (٠-٩)
// with the Arabic thousands separator (٬); EN → classic `1,234`.
const fmt = (n, isAR = false) => {
  const locale = isAR ? "ar-EG" : "en-US";
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n || 0).replace(/\u066C/g, ",");
};

export default function Market2026Dialog({ open, onClose }) {
  const { lang } = useLang();
  const { format, currency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const closeRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setFailed(false);
    api.get("/market/monthly-2026")
      .then((r) => { if (r?.data?.ytd) setData(r.data); else setFailed(true); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [open, attempt]);

  // Focus the close button on open; restore focus to the opener on close.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const t = setTimeout(() => closeRef.current?.focus(), 0);
    return () => { clearTimeout(t); opener?.focus?.(); };
  }, [open]);

  // Add body class so the FloatingActions stack + AI-tooltip disappear
  // while the modal is open (they were fighting for the same right edge).
  useEffect(() => {
    if (open) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        // Keep keyboard focus inside the dialog.
        const box = document.querySelector("[data-testid=market-2026-panel]");
        const f = box ? [...box.querySelectorAll("button, a[href], [tabindex]:not([tabindex='-1'])")] : [];
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-propertic-navy/60 backdrop-blur-sm flex items-start justify-center pt-28 sm:pt-32 pb-4 sm:pb-6 px-3 sm:px-6"
      onClick={onClose}
      data-testid="market-2026-dialog"
    >
      <div
        className="relative bg-propertic-paper-2 rounded-[1.5rem] sm:rounded-[2rem] w-full max-w-4xl max-h-[calc(100dvh-6rem)] sm:max-h-[calc(100dvh-8rem)] flex flex-col overflow-hidden border hairline shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="market-2026-title"
        data-testid="market-2026-panel"
      >
        <div className="p-5 sm:p-7 lg:p-8 relative overflow-y-auto flex-1 overscroll-contain">
          <button
            ref={closeRef}
            onClick={onClose}
            data-testid="market-2026-close"
            className="absolute top-4 end-4 w-9 h-9 rounded-full border hairline grid place-items-center text-propertic-navy bg-white/90 backdrop-blur hover:bg-propertic-navy hover:text-white transition-colors z-10 shadow-md"
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
          >
            <X className="w-4 h-4" />
          </button>

          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-2">{lang === "ar" ? "دبي · البيانات المفتوحة لدائرة الأراضي" : "Dubai · Official DLD open data · monthly"}</p>
          <h2 id="market-2026-title" className="font-editorial text-xl sm:text-2xl md:text-3xl leading-[1.15] text-propertic-ink pr-10">
            {lang === "ar" ? "٢٠٢٦" : "2026"} <span className="font-italic-serif text-propertic-gold">{lang === "ar" ? "شهراً بشهر." : "month-by-month."}</span>
          </h2>

          {failed && !data ? (
            <div className="py-20 text-center" data-testid="market-2026-error">
              <p className="text-sm text-propertic-ink-2">{lang === "ar" ? "تعذّر تحميل بيانات ٢٠٢٦ حالياً." : "2026 data could not be loaded right now."}</p>
              <button type="button" onClick={() => setAttempt((n) => n + 1)} data-testid="market-2026-retry" className="mt-4 rounded-full bg-propertic-navy px-5 py-2 text-xs text-white">{lang === "ar" ? "إعادة المحاولة" : "Try again"}</button>
            </div>
          ) : loading || !data ? (
            <div className="py-24 text-center text-propertic-muted font-mono-brand">{lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</div>
          ) : (
            <>
              {/* Top row , SALES is the hero metric, volume shown small */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3">
                <Stat
                  label={lang === "ar" ? "مبيعات ٢٠٢٦ حتى اليوم" : "YTD 2026 sales"}
                  value={format((data.ytd.sales_value_aed_bn || 0) * 1_000_000_000, { compact: true, decimals: 2 })}
                  accent
                />
                <Stat
                  label={lang === "ar" ? "صفقات البيع حتى اليوم" : "YTD sales txns"}
                  value={fmt(data.ytd.sales_transactions, lang === "ar")}
                />
                <Stat
                  label={lang === "ar" ? "قيد الإنشاء / جاهزة" : "Off-plan / Ready"}
                  value={
                    lang === "ar"
                      // In RTL, the label reads "off-plan / ready" (right→left).
                      // The value is `force-ltr` so digits stay Latin, swap the
                      // order in the source string so an RTL reader lands on
                      // off-plan first, matching the label.
                      ? `${data.ytd.ready_share_pct || "-"}% / ${data.ytd.off_plan_share_pct || "-"}%`
                      : `${data.ytd.off_plan_share_pct || "-"}% / ${data.ytd.ready_share_pct || "-"}%`
                  }
                />
                <Stat
                  label={lang === "ar" ? "مبيعات ٢٠٢٥ (رسمي)" : "2025 sales (official)"}
                  value={format(((data.annual_2025_reference?.sales_value_aed_bn) || 682.5) * 1_000_000_000, { compact: true, decimals: 1 })}
                />
              </div>

              {/* H1 Rollup breakdown: sales / txns / volume */}
              <div className="mt-5 grid grid-cols-3 gap-2.5 md:gap-3">
                <MiniStat
                  label={lang === "ar" ? "إجمالي المبيعات" : "Total sales"}
                  value={format((data.ytd.sales_value_aed_bn || 0) * 1_000_000_000, { compact: true, decimals: 2 })}
                  hint={lang === "ar" ? "بيع فعلي" : "pure sales"}
                />
                <MiniStat
                  label={lang === "ar" ? "إجمالي الإجراءات" : "Total procedures"}
                  value={fmt(data.ytd.transactions, lang === "ar")}
                  hint={lang === "ar" ? "بيع + رهون + هبات" : "sales + mortgages + gifts"}
                />
                <MiniStat
                  label={lang === "ar" ? "إجمالي الحجم" : "Total volume"}
                  value={format((data.ytd.volume_aed_bn || 0) * 1_000_000_000, { compact: true, decimals: 2 })}
                  hint={lang === "ar" ? "بيع + رهن + هبات" : "sales + mortgages + gifts"}
                />
              </div>

              {/* Off-plan / Ready split (per DLD H1 headline) */}
              <div className="mt-4 grid grid-cols-2 gap-2.5 md:gap-3">
                <div className="rounded-xl p-3 bg-propertic-navy text-propertic-paper">
                  <p className="font-mono-brand text-[9px] tracking-[0.18em] uppercase text-propertic-gold-2">{lang === "ar" ? "قيد الإنشاء" : "Off-plan"}</p>
                  <p className="font-editorial text-xl mt-1 tabular force-ltr">{data.ytd.off_plan_share_pct}%</p>
                  <p className="text-[9px] mt-0.5 text-propertic-paper/50 font-mono-brand tracking-[0.12em] uppercase">{lang === "ar" ? "حصة من عدد صفقات البيع" : "share of sales count"}</p>
                  <p className="text-[11px] mt-1 text-propertic-paper/60 tabular force-ltr">{data.ytd.off_plan_value_aed_bn != null ? `AED ${data.ytd.off_plan_value_aed_bn.toFixed(2)}B` : "—"}</p>
                </div>
                <div className="rounded-xl p-3 bg-white border hairline">
                  <p className="font-mono-brand text-[9px] tracking-[0.18em] uppercase text-propertic-gold">{lang === "ar" ? "جاهزة / ثانوية" : "Ready / secondary"}</p>
                  <p className="font-editorial text-xl mt-1 tabular force-ltr text-propertic-navy">{data.ytd.ready_share_pct}%</p>
                  <p className="text-[9px] mt-0.5 text-propertic-muted/80 font-mono-brand tracking-[0.12em] uppercase">{lang === "ar" ? "حصة من عدد صفقات البيع" : "share of sales count"}</p>
                  <p className="text-[11px] mt-1 text-propertic-muted tabular force-ltr">{data.ytd.ready_value_aed_bn != null ? `AED ${data.ytd.ready_value_aed_bn.toFixed(2)}B` : "—"}</p>
                </div>
              </div>

              {/* Per-quarter table , sales + volume side by side */}
              <div className="mt-4 bg-white rounded-[1.25rem] border hairline overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]" data-testid="market-2026-table">
                  <thead>
                    <tr className="bg-propertic-sand/60 text-propertic-muted">
                      <th className="text-start p-2.5 font-mono-brand text-[10px] tracking-[0.15em] uppercase">{lang === "ar" ? "الشهر" : "Month"}</th>
                      <th className="text-end p-2.5 font-mono-brand text-[10px] tracking-[0.15em] uppercase">{lang === "ar" ? "المبيعات (مليار)" : "Sales (B)"}</th>
                      <th className="text-end p-2.5 font-mono-brand text-[10px] tracking-[0.15em] uppercase">{lang === "ar" ? "صفقات البيع" : "Sales txns"}</th>
                      <th className="text-end p-2.5 font-mono-brand text-[10px] tracking-[0.15em] uppercase">{lang === "ar" ? "الحجم (مليار)" : "Volume (B)"}</th>
                      <th className="text-end p-2.5 font-mono-brand text-[10px] tracking-[0.15em] uppercase">{lang === "ar" ? "المصدر" : "Source"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {(() => {
                      // Detect the in-progress month = last row that carries a
                      // month == current data.current_month. When present, we
                      // append a small "through {end_date}" note under its row
                      // so a 6.2B / 2,214 sales row doesn't read as a complete
                      // month. `data.current_month_window` is a small envelope
                      // the backend already exposes (start_date, end_date,
                      // iso_week). If missing, we quietly render the row as
                      // before, no annotation.
                      const currentYm = data.current_month || (data.months?.[data.months.length - 1]?.month);
                      const cw = data.current_month_window || null;
                      return data.months.map((m) => {
                        const monthEnd = (ym) => { const [y, mo] = ym.split("-").map(Number); return new Date(Date.UTC(y, mo, 0)).toISOString().slice(0, 10); };
                        const isCurrent = m.month === currentYm && (cw?.end_date ? cw.end_date < monthEnd(m.month) : (m.sales_transactions == null || m.sales_transactions < 5000));
                        return (
                          <tr key={m.month} className="hover:bg-propertic-sand/30 transition-colors" data-testid={`market-2026-row-${m.month}`}>
                            <td className="p-2.5 font-editorial text-sm text-propertic-navy">
                              {m.month}
                              {isCurrent && (
                                <span className="ms-2 inline-block font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-gold whitespace-nowrap" data-testid={`market-2026-partial-${m.month}`}>
                                  {lang === "ar" ? "· حتى " : "· through "}
                                  <bdi dir="ltr">{cw?.end_date || "—"}{cw?.iso_week ? ` · ${cw.iso_week}` : ""}</bdi>
                                </span>
                              )}
                            </td>
                            <td className="p-2.5 text-end tabular text-propertic-gold force-ltr font-semibold text-xs">{m.sales_aed_bn ?? "-"}</td>
                            <td className="p-2.5 text-end tabular text-propertic-ink force-ltr text-xs">{m.sales_transactions ? fmt(m.sales_transactions, lang === "ar") : "-"}</td>
                            <td className="p-2.5 text-end tabular text-propertic-navy/70 force-ltr text-xs">{m.volume_aed_bn}</td>
                            <td className="p-2.5 text-end">
                              <span className="inline-block font-mono-brand px-2 py-0.5 rounded-full text-emerald-700 bg-emerald-50 border border-emerald-100 text-[9px]">
                                DLD
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Explanation: sales vs volume */}
              <div className="mt-4 rounded-xl bg-propertic-sand/40 border hairline p-3">
                <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-1.5">{lang === "ar" ? "المبيعات مقابل الحجم" : "Sales vs Volume"}</p>
                <p className="text-[12px] text-propertic-ink-2/95 leading-relaxed">
                  {lang === "ar"
                    ? "«المبيعات» = صفقات بيع عقارية فعلية فقط (سكني + تجاري). «الحجم» = جميع الإجراءات المسجّلة لدى دائرة الأراضي بما فيها الرهون العقارية والهبات وتسجيل النقل. للمستثمر المهم هو رقم المبيعات."
                    : "\"Sales\" = actual property purchase transactions only (residential + commercial). \"Volume\" = every procedure registered with DLD, sales plus mortgage registrations plus gift transfers plus other transfers. For an investor, sales is the number that matters."}
                </p>
              </div>

              <p className="mt-3 text-[11px] text-propertic-muted leading-relaxed">
                <TrendingUp className="w-3 h-3 inline me-1.5 text-propertic-gold" />
                {lang === "ar"
                  ? "الأرقام مأخوذة مباشرةً من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك في دبي (DLD Open Data)، وتُحدَّث كل اثنين ١١:٠٠ بتوقيت الإمارات المحلي. لا نستخدم أي مصدر آخر."
                  : "Figures come directly from the Dubai Land Department Open Data Gateway (dubailand.gov.ae), refreshed every Monday 11:00 UAE local time. No other source is used."}
                {data.source_url && (
                  <>
                    {" "}
                    <a href={data.source_url} target="_blank" rel="noreferrer" className="text-propertic-gold hover:underline">
                      {lang === "ar" ? "المصدر الرسمي" : "Official source"}
                    </a>
                  </>
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                <a href="https://dubailand.gov.ae/en/news-media/" target="_blank" rel="noreferrer" className="text-[11px] text-propertic-navy hover:text-propertic-gold font-medium">Dubai Land Department , News & Media</a>
                <a href="https://www.mediaoffice.ae/en" target="_blank" rel="noreferrer" className="text-[11px] text-propertic-navy hover:text-propertic-gold font-medium">Dubai Media Office</a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// (Cell import removed , bars now use uniform reported-only color)

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? "bg-propertic-navy text-propertic-paper" : "bg-white border hairline"}`}>
      <p className={`font-mono-brand text-[9px] tracking-[0.18em] uppercase ${accent ? "text-propertic-gold-2" : "text-propertic-muted"}`}>{label}</p>
      <p className={`font-editorial text-lg sm:text-xl mt-1 tabular force-ltr ${accent ? "text-propertic-paper" : "text-propertic-navy"}`}>{value}</p>
    </div>
  );
}

function MiniStat({ label, value, hint }) {
  return (
    <div className="rounded-xl bg-white border hairline p-3">
      <p className="font-mono-brand text-[9px] tracking-[0.18em] uppercase text-propertic-muted">{label}</p>
      <p className="font-editorial text-base sm:text-lg mt-1 tabular force-ltr text-propertic-navy">{value}</p>
      <p className="text-[10px] text-propertic-muted/80 mt-0.5">{hint}</p>
    </div>
  );
}
